/*───────────────────────────────────────────────────────────────
 🌐 Project: Ring2All Platform
 🧑‍💻 Author: Rodrigo Cuadra
 🏢 Company: Ring2All LLC
 📄 File: useFaxLogs.ts
 🗓️ Created: 2025-12-03
 🧩 Description:
    Hook for managing fax logs data.
    Supports Domain Contextual Filtering pattern.

 🔁 Last Updated: 2025-01-03 by Rodrigo Cuadra (Added Domain Filtering)
────────────────────────────────────────────────────────────────*/

import { useCallback, useEffect } from 'react';
import { useI18n } from '../../../hooks/useI18n';
import { useNotifications } from '../../../contexts/NotificationContext';
import { useDataGrid } from '../../../hooks/useDataGrid';
import { useContextStore } from '../../../store/context';
import { fetchFaxLogs } from '../api/faxViewerApi';
import type { FaxLog } from '../types';

interface UseFaxLogsProps {
  domainId?: number;
  onRefreshNeeded?: () => void;
}

export function useFaxLogs(props: UseFaxLogsProps = {}) {
  const { t } = useI18n();
  const { showError } = useNotifications();
  const { selectedDomain } = useContextStore();

  // Use provided domainId or fallback to selected domain
  const effectiveDomainId = props.domainId ?? selectedDomain?.id;

  const fetchData = useCallback(
    async (params: {
      page: number;
      limit: number;
      search?: string;
      sortBy?: string;
      sortOrder?: 'asc' | 'desc';
      filters?: Record<string, any>;
    }) => {
      try {
        const result = await fetchFaxLogs(
          {
            page: params.page,
            limit: params.limit,
            search: params.search,
            sortBy: params.sortBy || 'createdAt',
            sortOrder: params.sortOrder || 'desc',
            faxDeviceId: params.filters?.faxDeviceId,
            direction: params.filters?.direction,
            success: params.filters?.success,
            startDate: params.filters?.startDate,
            endDate: params.filters?.endDate,
          },
          effectiveDomainId
        );

        return {
          data: result.data,
          pagination: result.pagination,
        };
      } catch (error) {
        showError(error.message || t('faxViewer:messages.loadError'));
        throw error;
      }
    },
    [showError, t, effectiveDomainId]
  );

  const {
    data,
    loading,
    columns,
    pagination,
    searchQuery,
    sortBy,
    sortOrder,
    handleSearch,
    handleSort,
    handlePageChange,
    handleItemsPerPageChange,
    handleColumnToggle,
    refresh,
  } = useDataGrid({
    fetchData,
    initialColumns: [],
    initialSortBy: 'createdAt',
    initialSortOrder: 'desc',
    initialLimit: 20,
    moduleName: 'faxViewer',
    reloadKey: effectiveDomainId ?? null,
  });

  // Trigger refresh when domainId changes
  useEffect(() => {
    if (effectiveDomainId !== undefined && props.onRefreshNeeded) {
      props.onRefreshNeeded();
    }
  }, [effectiveDomainId]);

  return {
    data,
    loading,
    columns,
    pagination,
    searchQuery,
    sortBy,
    sortOrder,
    handleSearch,
    handleSort,
    handlePageChange,
    handleItemsPerPageChange,
    handleColumnToggle,
    refresh,
    effectiveDomainId,
  };
}

